import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FormRenderProps } from 'react-final-form';

import { useSelector } from 'hooks';
import PageBaseProps from 'types/PageBase';
import GenericQuestion from 'types/Question';
import Carousel from 'components/ui/Carousel';
import PageFooter from 'components/ui/PageFooter';
import FormKeys from 'modules/form/types/FormKeys';
import { getOrigin } from 'modules/dealers/selectors';
import Questions from 'components/Questions/Questions';
import { getStepsConfig } from 'modules/selfServices/stepsConfigSelectors';
import { QuestionsForm as QuestionsFormType } from 'types/QuestionsForm';
import { useUpdateSelfServiceMutation } from 'modules/selfServices/service';
import { isKioskPreferenceQuestionVisible } from 'modules/selfServices/utils';
import { getKioskExplanationMedias, getSelfServiceType } from 'modules/selfServices/selectors';
import { KioskPreferencesQuestionsIds, UPDATE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';

// eslint-disable-next-line max-len
type KioskUsageRenderProps = FormRenderProps<QuestionsFormType> & Pick<PageBaseProps, 'onPrev' | 'shouldDisplayBackButton' | 'isPenultimate'> & { formKey: FormKeys };

const KioskUsageRender: React.FC<KioskUsageRenderProps> = ({
  onPrev, formKey, handleSubmit, hasValidationErrors, shouldDisplayBackButton, isPenultimate,
}) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const config = useSelector(getStepsConfig);
  const origin = useSelector(getOrigin);
  const type = useSelector(getSelfServiceType);
  const medias = useSelector(getKioskExplanationMedias);

  const [, { isLoading: isUpdateLoading }] = useUpdateSelfServiceMutation({ fixedCacheKey: UPDATE_FIXED_CACHE_KEY });

  const handleCarouselOpen = React.useCallback(() => setIsFullscreen(true), []);
  const handleCarouselClose = React.useCallback(() => setIsFullscreen(false), []);

  const isQuestionVisible = React.useCallback(
    (question: GenericQuestion) => isKioskPreferenceQuestionVisible(question.id as KioskPreferencesQuestionsIds, type, origin, config),
    [config, origin, type],
  );

  return (
    <div className="main-content max-w-content">
      <main className="content">
        <span className="text-center">
          <FormattedMessage
            id="page.kioskUsage.description"
            defaultMessage="You can use our kiosk to drop/retrieve your keys 24/7."
          />
        </span>
        <div className="gap-5">
          {medias?.length > 0 && (
            <Carousel
              medias={medias}
              className="rounded-card"
              carouselClassName="!mb-s"
              fullscreen={isFullscreen}
              onClose={handleCarouselClose}
              onMediaClick={handleCarouselOpen}
            />
          )}
          <Questions name={formKey} questionFilter={isQuestionVisible} />
        </div>
        <div className="text-center">
          <FormattedMessage
            tagName="span"
            id="page.kioskUsage.footer"
            // eslint-disable-next-line max-len
            defaultMessage="Your dealership will do their best to respect your wishes. You may change you mind by contacting your dealership."
          />
        </div>
      </main>
      <PageFooter
        onNext={handleSubmit}
        onPrev={onPrev}
        disabledStyle={hasValidationErrors}
        shouldDisplayBackButton={shouldDisplayBackButton}
        isPenultimate={isPenultimate}
        loading={isUpdateLoading}
      />
    </div>
  );
};

export default KioskUsageRender;
